import { customersQuery } from "@common/api"
import type {
  WorkPackageKind,
  WorkPackagePriority,
  WorkPackageStatus,
} from "@common/client"
import type {
  FilterFieldConfig,
  FilterOptionValue,
} from "@common/components/filters/filter-config"
import { useQuery } from "@tanstack/react-query"
import { createElement } from "react"
import { CUSTOMER_LIMIT } from "@/routes/_layout/quoting_/quotes/-components/filters/filter-model"
import { WorkOrderStatusIcon } from "@/routes/_layout/work-orders/-components/status-icon"
import CategoryOutline from "~icons/material-symbols/category-outline"
import FlightLand from "~icons/material-symbols/flight-land"
import GroupOutline from "~icons/material-symbols/group-outline"
import NotificationsOutline from "~icons/material-symbols/notifications-outline"
import RadioButtonChecked from "~icons/material-symbols/radio-button-checked"
import type { WorkOrdersSearch } from "./search"
import {
  KINDS,
  kindLabel,
  PRIORITIES,
  priorityLabel,
  STATUSES,
  statusLabel,
} from "./status"

export type WorkOrderFilterValues = Pick<
  WorkOrdersSearch,
  "status" | "priority" | "kind" | "subscribed" | "customer"
>

export const NO_WORK_ORDER_FILTERS: WorkOrderFilterValues = {
  status: undefined,
  priority: undefined,
  kind: undefined,
  subscribed: undefined,
  customer: undefined,
}

export function hasWorkOrderFilters(values: WorkOrderFilterValues): boolean {
  return Boolean(
    values.status?.length ||
      values.priority?.length ||
      values.kind?.length ||
      values.subscribed !== undefined ||
      values.customer?.length,
  )
}

function isStatus(value: FilterOptionValue): value is WorkPackageStatus {
  return STATUSES.some((status) => status === value)
}

function isPriority(value: FilterOptionValue): value is WorkPackagePriority {
  return PRIORITIES.some((priority) => priority === value)
}

function isKind(value: FilterOptionValue): value is WorkPackageKind {
  return KINDS.some((kind) => kind === value)
}

const statusFilter: FilterFieldConfig<WorkOrderFilterValues> = {
  key: "status",
  label: "Status",
  icon: RadioButtonChecked,
  type: "multiselect",
  searchPlaceholder: "Search statuses",
  emptyLabel: "No statuses",
  useOptions: () => ({
    options: STATUSES.map((status) => ({
      value: status,
      label: statusLabel(status),
      icon: createElement(WorkOrderStatusIcon, { status }),
    })),
    isLoading: false,
  }),
  get: (values) => values.status,
  set: (next) => ({ status: next?.filter(isStatus) }),
}

const priorityFilter: FilterFieldConfig<WorkOrderFilterValues> = {
  key: "priority",
  label: "Priority",
  icon: FlightLand,
  type: "multiselect",
  searchPlaceholder: "Search priorities",
  emptyLabel: "No priorities",
  useOptions: () => ({
    options: PRIORITIES.map((priority) => ({
      value: priority,
      label: priorityLabel(priority),
    })),
    isLoading: false,
  }),
  get: (values) => values.priority,
  set: (next) => ({ priority: next?.filter(isPriority) }),
}

const kindFilter: FilterFieldConfig<WorkOrderFilterValues> = {
  key: "kind",
  label: "Kind",
  icon: CategoryOutline,
  type: "multiselect",
  searchPlaceholder: "Search kinds",
  emptyLabel: "No kinds",
  useOptions: () => ({
    options: KINDS.map((kind) => ({ value: kind, label: kindLabel(kind) })),
    isLoading: false,
  }),
  get: (values) => values.kind,
  set: (next) => ({ kind: next?.filter(isKind) }),
}

const subscribedFilter: FilterFieldConfig<WorkOrderFilterValues> = {
  key: "subscribed",
  label: "Subscribed",
  icon: NotificationsOutline,
  type: "boolean",
  trueLabel: "Subscribed",
  falseLabel: "Not subscribed",
  get: (values) => values.subscribed,
  set: (next) => ({ subscribed: next }),
}

const customerFilter: FilterFieldConfig<WorkOrderFilterValues> = {
  key: "customer",
  label: "Customer",
  icon: GroupOutline,
  type: "multiselect",
  searchPlaceholder: "Search customers",
  emptyLabel: "No customers",
  useOptions: () => {
    const { data, isLoading } = useQuery(
      customersQuery({ skip: 0, limit: CUSTOMER_LIMIT }),
    )
    return {
      options: (data?.data ?? []).map((customer) => ({
        value: customer.id,
        label: customer.name ?? customer.email ?? customer.id,
      })),
      isLoading,
    }
  },
  get: (values) => values.customer,
  set: (next) => ({ customer: next?.map(String) }),
}

export const workOrderFilterConfigs: FilterFieldConfig<WorkOrderFilterValues>[] =
  [statusFilter, priorityFilter, kindFilter, customerFilter, subscribedFilter]
